import React, { ChangeEvent, useCallback, useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { MAIN_PAGE, NavbarMenuType, NOTIFICATION, route } from 'routes'
import { Link as RouterLink, useNavigate } from 'react-router-dom'
import clsx from 'clsx'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import Typography from '@mui/material/Typography'
import Badge from '@mui/material/Badge'
import PersonIcon from '@mui/icons-material/Person'
import PublicIcon from '@mui/icons-material/Public'
import HomeIcon from '@mui/icons-material/Home'
import NotificationsIcon from '@mui/icons-material/Notifications'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import Button, { ButtonProps } from 'components/Button'
import Skeleton from 'components/Skeleton'
import { NavbarDesktopProps } from '.'
import DropdownMenu from 'components/DropdownMenu'
import { FormDialog } from 'duck/AppDuck/DialogDuck'

import stylesGlobal from 'stylesGlobal.module.scss'
import styles from './NavBarGeneral.module.scss'

const classNameNavBarLabel = 'flex items-center'
const tabIndicatorProps = { className: 'h-0.5' }
const classesBadge = {
  badge: styles.BadgeInner
}
const classesNavbarDropdownMenu = {
  menu: styles.NavBarDropdownMenu,
  popper: 'w-full pt-0.5'
}

const tabIndicatorPropsNavBarDesktopSkeleton = {
  hidden: true
}

const NavBarDesktopSkeleton: React.FC<{
  isAuthorized?: boolean
}> = props => {
  const { isAuthorized } = props
  const skeletonMenuArray = isAuthorized ? [...Array(3)] : [...Array(2)]

  return (
    <Tabs
      className={clsx('relative', styles.NavBarTabs)}
      value=""
      variant="fullWidth"
      TabIndicatorProps={tabIndicatorPropsNavBarDesktopSkeleton}
    >
      <Tab value={''} className={clsx('mx-0 min-w-0 p-0', styles.NavBarTab)} />
      {skeletonMenuArray.map((e, i) => (
        <Tab
          key={i}
          className={styles.NavBarTab}
          label={
            <Typography className={clsx('w-full px-4', styles.NavBarTabText)} variant="button">
              <Skeleton fullWidth />
            </Typography>
          }
        />
      ))}

      {isAuthorized ? (
        <Tab
          className={clsx(stylesGlobal.BorderLeftOnSurface, 'min-w-14')}
          label={<Skeleton variant="rectangular" width={24} />}
        />
      ) : null}
    </Tabs>
  )
}

const NavBarDesktop: React.FC<NavbarDesktopProps> = props => {
  const {
    activeMenu,
    isAuthorized,
    loading,
    openDialog,
    userAlias,
    setActiveMenu,
    showMainMenu,
    userName,
    unreadCount
  } = props

  const navigate = useNavigate()
  const initiated = loading === false
  const activeMenuAdjusted =
    !isAuthorized &&
    (activeMenu === route.PROFILE.getUrl() ||
      activeMenu === route.ARTISTS.getUrl() ||
      activeMenu === NOTIFICATION)
      ? 0
      : activeMenu === route.ARTISTS.getUrl()
        ? route.PROFILE.getUrl()
        : activeMenu

  const exploreUrl = route.EXPLORE.getUrl()
  const handleChange = (event: ChangeEvent<any>, newValue: NavbarMenuType) => {
    setActiveMenu(newValue)
  }

  const ButtonDropdown = useCallback(
    (props: Pick<ButtonProps, 'disabled' | 'onClick'> & { buttonRef?: any }) => {
      const { buttonRef, ...restProps } = props
      return (
        <Button
          ref={buttonRef}
          {...restProps}
          className={clsx('h-full bg-primary', styles.NavBarTab)}
        >
          <PersonIcon className="mr-2 !text-base" />

          <Typography className={clsx('mr-auto', styles.NavBarTabText)} variant="button" noWrap>
            {userName}
          </Typography>

          <ExpandMoreIcon className="ml-1" />
        </Button>
      )
    },
    [userName]
  )
  const navBarDropdownItems = useMemo(() => {
    return [
      {
        key: 'profile',
        label: 'My Profile',
        onClick: () => {
          setActiveMenu(route.PROFILE.getUrl())
          navigate(route.ARTISTS.getUrl({ artistUrl: userAlias }))
        }
      },
      {
        key: 'account',
        label: 'Account',
        onClick: () => {
          setActiveMenu(route.PROFILE.getUrl())
          navigate(route.PROFILE.getUrl())
        }
      },
      {
        key: 'help',
        label: 'Help',
        onClick: () => {
          openDialog({ [FormDialog.SEND_QUESTION]: { dialogName: FormDialog.SEND_QUESTION } })
        }
      }
    ]
  }, [navigate, openDialog, setActiveMenu, userAlias])

  if (!showMainMenu) return null

  if (!initiated) return <NavBarDesktopSkeleton isAuthorized={isAuthorized} />

  return (
    <>
      <Tabs
        className={styles.NavBarTabs}
        value={activeMenuAdjusted}
        variant="fullWidth"
        onChange={handleChange}
        TabIndicatorProps={tabIndicatorProps}
      >
        {isAuthorized ? (
          <Tab
            className={styles.NavBarTab}
            component={RouterLink}
            to={MAIN_PAGE}
            key={MAIN_PAGE}
            value={MAIN_PAGE}
            label={
              <span className={classNameNavBarLabel}>
                <HomeIcon className="mr-2 !text-base" />
                <Typography className={styles.NavBarTabText} variant="button">
                  Home
                </Typography>
              </span>
            }
          />
        ) : (
          <Tab value={MAIN_PAGE}></Tab>
        )}

        <Tab
          className={styles.NavBarTab}
          component={RouterLink}
          to={exploreUrl}
          key={exploreUrl}
          value={exploreUrl}
          label={
            <span className={classNameNavBarLabel}>
              <PublicIcon className="mr-2 !text-base" />
              <Typography className={styles.NavBarTabText} variant="button">
                Explore
              </Typography>
            </span>
          }
        />

        {/* Used for Dropdown width placeholder */}
        {isAuthorized ? (
          <Tab
            className={clsx('pointer-events-none opacity-0', styles.NavBarTab)}
            component={RouterLink}
            value={route.PROFILE.getUrl()}
            to={route.PROFILE.getUrl()}
            key={route.PROFILE.getUrl()}
            label={
              <span className={twMerge('pr-7', classNameNavBarLabel)}>
                <PersonIcon className="mr-2 !text-base" />
                <Typography className={styles.NavBarTabText} variant="button" noWrap>
                  {userName}
                </Typography>
              </span>
            }
          />
        ) : null}
        {/* Used for Dropdown width placeholder END */}

        {isAuthorized ? (
          <Tab
            className={clsx(stylesGlobal.BorderLeftOnSurface, 'min-w-14')}
            key={NOTIFICATION}
            value={NOTIFICATION}
            onClick={() => {
              setActiveMenu(activeMenu !== NOTIFICATION ? NOTIFICATION : '')
            }}
            label={
              <span className={classNameNavBarLabel}>
                {unreadCount ? (
                  <Badge
                    badgeContent={
                      <Typography variant="button" component="span">
                        {unreadCount > 100 ? '99+' : unreadCount}
                      </Typography>
                    }
                    color="error"
                    classes={classesBadge}
                  />
                ) : (
                  <NotificationsIcon />
                )}
              </span>
            }
          />
        ) : null}

        <Tab
          className={clsx(isAuthorized && 'hidden', styles.NavBarTab)}
          component={RouterLink}
          to={route.SIGN_IN.getUrl()}
          key={route.SIGN_IN.getUrl()}
          value={route.SIGN_IN.getUrl()}
          label={
            <span className={classNameNavBarLabel}>
              <PersonIcon className="mr-2 !text-base" />
              <Typography className={styles.NavBarTabText} variant="button">
                LOGIN
              </Typography>
            </span>
          }
        />
      </Tabs>

      {isAuthorized ? (
        <DropdownMenu
          placement="bottom-end"
          color="primary"
          CustomButtonComponent={ButtonDropdown}
          className={clsx('!absolute top-0', styles.NavBarDropdown)}
          classes={classesNavbarDropdownMenu}
          items={navBarDropdownItems}
          disablePortal
        />
      ) : null}
    </>
  )
}

export default NavBarDesktop
